import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { readVoicePref } from '../lib/azureVoices'

// Wraps text-to-speech for the whole app. Two-tier strategy:
//
//   1. Try Azure Cognitive Services Speech via the tts-azure edge
//      function. Returns MP3 audio bytes, played via an <audio>
//      element. Neural voices (en-US-AvaNeural, ar-EG-SalmaNeural by
//      default) sound dramatically better than what iOS Safari
//      exposes to the web Speech API.
//   2. Fall back to the browser SpeechSynthesis API if Azure fails
//      (quota exhausted, network error, signed-out, etc.).
//
// Returns the same contract as before — pages don't need to change:
//   - play(text, lang)     — fire-and-forget; async work happens
//                            inside, state updates via events
//   - pause() / resume()
//   - stop()
//   - playing / paused / supported
//   - rate / setRate       — 0.5-2x, persisted to localStorage; the
//                            Audio element supports playbackRate so
//                            both paths honour it
//
// Audio is cached in-memory per session keyed by `${lang}|${text}` so
// re-plays of the same word are free (no Azure round-trip, no quota).

const RATE_STORAGE_KEY = 'phoenix:tts-rate'

// iOS / macOS expose a handful of "Enhanced" / "Premium" voices that
// sound dramatically better than the defaults — but only once the
// user has downloaded them via Settings → Accessibility → Spoken
// Content → Voices. Browsers also expose a plain default voice and
// sometimes remote network voices that lag and sound canned.
//
// Quality signals, ranked highest to lowest, applied to whatever
// voices the platform actually exposes:
//   1. Name contains "Premium" / "Enhanced" / "Neural" — explicit
//      quality tier markers iOS uses since iOS 16.
//   2. Named-known-good voices (Samantha, Ava, Aaron, Daniel, Karen,
//      Moira, Allison, Susan, Tom, Fred for English; Maged, Tarik
//      for Arabic). These are the Apple-curated voices, usually
//      higher quality than third-party defaults.
//   3. localService=true — on-device synth, much lower latency than
//      remote network voices and consistent quality across sessions.
//   4. Exact lang match (en-US, ar-SA) preferred over partial (en).
//   5. default voice as a last resort.
const HIGH_QUALITY_EN_NAMES = [
  'samantha', 'ava', 'aaron', 'daniel', 'karen', 'moira', 'allison',
  'susan', 'tom', 'fred', 'evan', 'nicky', 'zoe', 'siri', 'voiceover',
]
const HIGH_QUALITY_AR_NAMES = ['maged', 'tarik', 'majed']

function voiceQualityScore(voice, want) {
  const name = (voice.name || '').toLowerCase()
  const lang = (voice.lang || '').toLowerCase()
  if (!lang.startsWith(want)) return 0
  let score = lang.startsWith(want + '-') ? 100 : 80
  if (name.includes('premium')) score += 60
  if (name.includes('enhanced')) score += 50
  if (name.includes('neural')) score += 40
  const known = want === 'ar' ? HIGH_QUALITY_AR_NAMES : HIGH_QUALITY_EN_NAMES
  if (known.some((n) => name.includes(n))) score += 25
  if (voice.localService) score += 15
  if (voice.default) score += 5
  return score
}

function rankedVoice(voices, want) {
  let best = null
  let bestScore = 0
  for (const v of voices) {
    const s = voiceQualityScore(v, want)
    if (s > bestScore) {
      best = v
      bestScore = s
    }
  }
  return best
}

// Module-level cache shared across hook consumers — the same word
// played on /english/speak and on a vocab card hits the same MP3.
// We hold blob URLs (object URLs) which the browser frees when the
// page navigates / closes the tab; we never revoke explicitly here
// because the cache lives as long as the session.
const azureAudioCache = new Map()

async function fetchAzureTts(text, lang, voice) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('not authenticated')
  const baseUrl = process.env.REACT_APP_SUPABASE_URL
  if (!baseUrl) throw new Error('SUPABASE_URL not configured')
  const url = `${baseUrl}/functions/v1/tts-azure`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
    },
    body: JSON.stringify({ text, lang, voice }),
  })
  if (!res.ok) {
    let detail = ''
    try { detail = await res.text() } catch { /* noop */ }
    throw new Error(`tts-azure ${res.status} ${detail.slice(0, 200)}`)
  }
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

export function useTextToSpeech() {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window
  const [playing, setPlaying] = useState(false)
  const [paused, setPaused] = useState(false)
  const [rate, setRateState] = useState(() => {
    try {
      const stored = parseFloat(localStorage.getItem(RATE_STORAGE_KEY) || '')
      return Number.isFinite(stored) && stored >= 0.5 && stored <= 2 ? stored : 1
    } catch { return 1 }
  })
  const [voices, setVoices] = useState([])
  const utteranceRef = useRef(null)
  const audioRef = useRef(null)
  const voicesDumpedRef = useRef(false)

  // Cache the available voices for the browser fallback path.
  useEffect(() => {
    if (!supported) return undefined
    const pull = () => {
      const list = window.speechSynthesis.getVoices()
      setVoices(list)
      if (!voicesDumpedRef.current && list.length > 0) {
        voicesDumpedRef.current = true
        // eslint-disable-next-line no-console
        console.log('[tts] browser voices available (' + list.length + '): ' + list
          .filter((v) => /^(en|ar)/i.test(v.lang || ''))
          .map((v) => `${v.name} [${v.lang}${v.localService ? ' local' : ''}${v.default ? ' default' : ''}]`)
          .join(', '))
      }
    }
    pull()
    window.speechSynthesis.addEventListener('voiceschanged', pull)
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', pull)
    }
  }, [supported])

  // Stop any in-flight audio on unmount.
  useEffect(() => () => {
    if (supported) {
      try { window.speechSynthesis.cancel() } catch { /* noop */ }
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause()
        audioRef.current.src = ''
      } catch { /* noop */ }
      audioRef.current = null
    }
  }, [supported])

  const setRate = useCallback((value) => {
    const clamped = Math.max(0.5, Math.min(2, Number(value) || 1))
    setRateState(clamped)
    try { localStorage.setItem(RATE_STORAGE_KEY, String(clamped)) } catch { /* private mode */ }
  }, [])

  const pickVoice = useCallback((lang) => {
    if (!voices.length) return null
    return rankedVoice(voices, lang === 'ar' ? 'ar' : 'en')
  }, [voices])

  // Tear down both playback paths so a fresh play() starts clean.
  const stopAll = useCallback(() => {
    if (supported) {
      try { window.speechSynthesis.cancel() } catch { /* noop */ }
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause()
        audioRef.current.src = ''
      } catch { /* noop */ }
      audioRef.current = null
    }
    utteranceRef.current = null
  }, [supported])

  // Browser fallback path — uses Web Speech API. Same logic the hook
  // had before Azure was added.
  const playBrowser = useCallback((text, lang) => {
    if (!supported || !text) return
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = rate
    utterance.pitch = 1
    utterance.volume = 1
    utterance.lang = lang === 'ar' ? 'ar-SA' : 'en-US'
    const voice = pickVoice(lang)
    if (voice) utterance.voice = voice
    // eslint-disable-next-line no-console
    console.log('[tts] play (browser) voice="' + (voice?.name || 'default') + '" lang="' + (voice?.lang || utterance.lang) + '" local=' + (voice?.localService ? 'yes' : 'no') + ' of ' + voices.length + ' available')
    utterance.onstart = () => { setPlaying(true); setPaused(false) }
    utterance.onend = () => { setPlaying(false); setPaused(false); utteranceRef.current = null }
    utterance.onerror = () => { setPlaying(false); setPaused(false); utteranceRef.current = null }
    utterance.onpause = () => setPaused(true)
    utterance.onresume = () => setPaused(false)
    utteranceRef.current = utterance
    window.speechSynthesis.speak(utterance)
  }, [supported, rate, pickVoice, voices.length])

  // Azure path. Returns true if playback started; false if anything
  // failed so play() can fall back to the browser. Voice preference
  // (Settings → Voice section) is read at call time so a change
  // applies on the very next play() without us needing to re-mount
  // every consumer.
  const playAzure = useCallback(async (text, lang) => {
    if (!text) return false
    const voice = readVoicePref(lang)
    // Include the chosen voice in the cache key so swapping voices
    // doesn't replay the old recording.
    const cacheKey = `${lang}|${voice}|${text}`
    let url = azureAudioCache.get(cacheKey)
    const cacheHit = !!url
    if (!url) {
      try {
        url = await fetchAzureTts(text, lang, voice)
        azureAudioCache.set(cacheKey, url)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('[tts] azure fetch failed: ' + (e?.message || String(e)) + ' — falling back to browser')
        return false
      }
    }
    // eslint-disable-next-line no-console
    console.log('[tts] play (azure) lang="' + lang + '" voice="' + voice + '" cache=' + (cacheHit ? 'hit' : 'miss'))
    const audio = new Audio(url)
    audio.playbackRate = rate
    audio.onplaying = () => { setPlaying(true); setPaused(false) }
    audio.onpause = () => setPaused(true)
    audio.onended = () => { setPlaying(false); setPaused(false); audioRef.current = null }
    audio.onerror = () => { setPlaying(false); setPaused(false); audioRef.current = null }
    audioRef.current = audio
    try {
      await audio.play()
      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('[tts] azure audio.play() failed: ' + (e?.message || String(e)) + ' — falling back')
      audioRef.current = null
      return false
    }
  }, [rate])

  const play = useCallback(async (text, lang) => {
    if (!text) return
    stopAll()
    // Try Azure first, fall back to browser TTS on any failure.
    const ok = await playAzure(text, lang)
    if (!ok) playBrowser(text, lang)
  }, [stopAll, playAzure, playBrowser])

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      try { audioRef.current.pause() } catch { /* noop */ }
    }
    if (supported) {
      try { window.speechSynthesis.pause() } catch { /* noop */ }
    }
  }, [supported])

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch(() => { /* user-gesture lost; let the caller restart */ })
    }
    if (supported) {
      try { window.speechSynthesis.resume() } catch { /* noop */ }
    }
  }, [supported])

  const stop = useCallback(() => {
    stopAll()
    setPlaying(false)
    setPaused(false)
  }, [stopAll])

  return useMemo(() => ({
    play, pause, resume, stop,
    playing, paused, supported,
    rate, setRate,
  }), [play, pause, resume, stop, playing, paused, supported, rate, setRate])
}
